import { Action, configureStore, ThunkAction, Tuple } from '@reduxjs/toolkit';
import {
  useSelector as useReduxSelector,
  useDispatch as useReduxDispatch,
  type TypedUseSelectorHook,
} from 'react-redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistCombineReducers,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import rootSaga from '~/sagas';
import { RootState } from '~/types';
import dynamicMiddlewares from './dynamic-middlewares';
import middlewares, { sagaMiddleware } from './middlewares';
import alerts, { alertsState } from './slices/alerts';
import app, { appState } from './slices/app';
import github, { githubState } from './slices/github';
import user, { userState } from './slices/user';
import form, { formState } from './slices/formSlice';
import estimateForm, { estimateFormState } from './slices/estimateFormSlice';
import utm, { utmState } from './slices/utm';
import plans, { plansState } from './slices/plans';
import userLimits, { userLimitsState } from './slices/userLimits';

const getDefaultMiddlewareOptions = {
  serializableCheck: {
    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
  },
  // thunk: false,
};

export const initialState = {
  alerts: alertsState,
  app: appState,
  github: githubState,
  user: userState,
  form: formState,
  utm: utmState,
  estimateForm: estimateFormState,
  plans: plansState,
  userLimits: userLimitsState,
};

export const reducers = {
  alerts,
  app,
  github,
  user,
  form,
  utm,
  estimateForm,
  plans,
  userLimits,
};

const rootReducer = persistCombineReducers<RootState>(
  {
    key: 'rrsb',
    stateReconciler: autoMergeLevel2,
    storage,
    blacklist: ['alerts', 'form', 'app', 'userLimits'],
    timeout: 0,
  },
  reducers,
);

export const configStore = (preloadedState: any = {}) => {
  const enhancedStore = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: getDefaultMiddleware => {
      return new Tuple(
        ...getDefaultMiddleware(getDefaultMiddlewareOptions),
        ...middlewares,
        dynamicMiddlewares,
      );
    },
  });

  sagaMiddleware.run(rootSaga);

  return enhancedStore;
};

export const store = configStore();
export const persistor = persistStore(store);

export const useDispatch = () => useReduxDispatch<ReduxDispatch>();
export const useSelector: TypedUseSelectorHook<ReduxState> = useReduxSelector;

/* Types */
export type ReduxStore = typeof store;
export type ReduxState = ReturnType<typeof store.getState>;
export type ReduxDispatch = typeof store.dispatch;
export type ReduxThunkAction<ReturnType = void> = ThunkAction<
  ReturnType,
  ReduxState,
  unknown,
  Action
>;
